/**
 * The Metric Definitions table's search and Filter, applied in memory: the
 * table loads every definition the org has, so narrowing them never goes
 * back to the server.
 */

import {
  filterInMemory,
  matchesBoolean,
  matchesDate,
  matchesOptions,
  matchesString,
  type FilterMatchers,
  type InMemoryFilterParams,
} from "@/shared/components/filters";
import type { FilterDefinition } from "@/shared/components/ResourceView";
import type { MetricDefinitionRecord } from "@/shared/domain/metrics";

const MATCHERS: FilterMatchers<MetricDefinitionRecord> = {
  name: (definition, filter) =>
    filter.type === "string" && matchesString(definition.name, filter),
  description: (definition, filter) =>
    filter.type === "string" && matchesString(definition.description, filter),
  unit: (definition, filter) =>
    filter.type === "enum" && matchesOptions(definition.unit, filter),
  has_description: (definition, filter) =>
    filter.type === "boolean" &&
    matchesBoolean(Boolean(definition.description?.trim()), filter),
  created: (definition, filter) =>
    filter.type === "date" && matchesDate(definition.created, filter),
  modified: (definition, filter) =>
    filter.type === "date" && matchesDate(definition.modified, filter),
};

/**
 * The definitions that pass the Filter's rows, among them the Name row the
 * quick search adds.
 */
export function filterDefinitions(
  definitions: readonly MetricDefinitionRecord[],
  params: InMemoryFilterParams,
): MetricDefinitionRecord[] {
  return filterInMemory(definitions, params, MATCHERS);
}

/** Distinct `values`, A to Z, numbers by value. */
function sortedDistinct(values: Iterable<string>): string[] {
  return [...new Set(values)].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

/**
 * The Filter's fields. Name suggests the org's metric names; Unit offers the
 * units in use, and "Not set" for none.
 */
export function definitionFilterFields(
  definitions: readonly MetricDefinitionRecord[],
): FilterDefinition[] {
  return [
    {
      type: "string",
      field: "name",
      name: "Name",
      description: "The metric's full dotted name",
      suggestions: {
        kind: "list",
        values: sortedDistinct(
          definitions.map((definition) => definition.name),
        ),
      },
    },
    {
      type: "string",
      field: "description",
      name: "Description",
      // Free text, which no list of values could cover, so "contains" leads.
      allowedComparators: [
        "CONTAINS",
        "NOT_CONTAINS",
        "LIKE",
        "NOT_LIKE",
        "EQUALS",
        "NOT_EQUALS",
      ],
    },
    {
      type: "enum",
      field: "unit",
      name: "Unit",
      nullable: true,
      options: sortedDistinct(
        definitions.flatMap((definition) => definition.unit ?? []),
      ),
    },
    {
      type: "boolean",
      field: "has_description",
      name: "Has description",
    },
    { type: "date", field: "created", name: "Created" },
    { type: "date", field: "modified", name: "Modified" },
  ];
}
